import React from 'react';
import type { PillTone } from './bench-model.js';

/**
 * Comp-faithful status pill (the design's `.badge`): a bordered, rounded-full
 * chip with a tone-colored translucent fill. Distinct from the app-wide
 * `components/ui/Badge` (square, used elsewhere) so the workforce surfaces match
 * the handoff exactly without restyling the shared Badge.
 */

const toneClass: Record<PillTone, string> = {
  ok: 'text-success-text bg-success-muted border-success-text/25',
  warn: 'text-warning-text bg-warning-muted border-warning-text/25',
  danger: 'text-danger-text bg-danger-muted border-danger-text/30',
  accent: 'text-accent-text bg-accent-muted border-accent-text/25',
  purple: 'text-purple-text bg-purple-muted border-purple-text/25',
  teal: 'text-teal-text bg-teal-muted border-teal-text/25',
  neutral: 'text-muted bg-surface border-border',
};

interface PillProps {
  tone: PillTone;
  /** Leading 6px currentColor dot (lifecycle / status badges). */
  dot?: boolean;
  size?: 'sm' | 'xs';
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

const sizeClass = {
  sm: 'gap-1.5 px-[9px] py-[3px] text-[11.5px]',
  xs: 'gap-1 px-[7px] py-px text-[10px]',
} as const;

export const Pill: React.FC<PillProps> = ({ tone, dot = false, size = 'sm', icon, children, className = '' }) => (
  <span
    className={`inline-flex items-center rounded-full border font-medium ${sizeClass[size]} ${toneClass[tone]} ${className}`}
  >
    {dot && (
      <span className={`${size === 'xs' ? 'h-1 w-1' : 'h-1.5 w-1.5'} rounded-full bg-current`} aria-hidden="true" />
    )}
    {icon}
    {children}
  </span>
);
